import { useMutation, UseQueryResult } from '@tanstack/react-query'
import { queryClient, QueryOptions, UseMutationResult, useResource, useResources } from '.'
import {
  createTwoParties,
  createThreeParties,
  fetch,
  get,
  PeriodSummary,
  FetchPeriodSummariesParams,
  TwoPartiesPeriodSummary,
  ThreePartiesPeriodSummary,
  SimulatePeriodSummary,
  PeriodSummaryUpdatePayload,
  update,
  simulateThreeParties,
  simulateTwoParties,
  SimulateTwoPartiesPeriodSummary,
  requestCancellation,
  resolveCancellationRequest,
  RequestCancellationBody,
  ResolveCancellationBody,
} from '../api/period-summaries'
import {
  CreateTwoPartiesParams,
  CreateThreePartiesParams,
} from '../../../backend/src/services/resources/period-summaries/period-summaries.controller.service.types'

export const periodSummaryKeys = {
  createTwoParties: ['period-summaries', 'two-parties', 'create'] as const,
  createThreeParties: ['period-summaries', 'three-parties', 'create'] as const,
  update: ['period-summaries', 'update'] as const,
  requestCancellation: ['period-summaries', 'request-cancellation'] as const,
  resolveCancellation: ['period-summaries', 'resolve-cancellation'] as const,
}

// Queries
/**
 * Hook pour récupérer un period summary par son ID
 */
export const usePeriodSummaryQuery = (id?: string): UseQueryResult<PeriodSummary, any> =>
  useResource('period-summaries', get, id)

/**
 * Hook pour récupérer la liste des period summaries avec filtres
 */
export const usePeriodSummariesQuery = (
  filters?: Partial<FetchPeriodSummariesParams>,
  options?: QueryOptions<PeriodSummary[]>,
): UseQueryResult<PeriodSummary[], any> =>
  useResources<PeriodSummary[], Partial<FetchPeriodSummariesParams>>(
    'period-summaries',
    fetch,
    filters || {},
    options,
  )

export const useSimulateThreePartiesPeriodSummaryQuery = (
  params: CreateThreePartiesParams,
  options?: QueryOptions<SimulatePeriodSummary>,
): UseQueryResult<SimulatePeriodSummary, any> =>
  useResources<SimulatePeriodSummary, any>(
    'period-summaries',
    simulateThreeParties,
    params,
    options,
  )

export const useSimulateTwoPartiesPeriodSummaryQuery = (
  params: CreateTwoPartiesParams,
  options?: QueryOptions<SimulateTwoPartiesPeriodSummary>,
): UseQueryResult<SimulateTwoPartiesPeriodSummary, any> =>
  useResources<SimulateTwoPartiesPeriodSummary, any>(
    'period-summaries',
    simulateTwoParties,
    params,
    options,
  )

// Mutations
/**
 * Hook pour créer un period summary à deux parties
 */
export const useCreateTwoPartiesMutation = (): UseMutationResult<
  TwoPartiesPeriodSummary,
  CreateTwoPartiesParams
> =>
  useMutation(createTwoParties, {
    mutationKey: periodSummaryKeys.createTwoParties,
    onSuccess: () => {
      invalidatePeriodSummariesQueries()
    },
  })

/**
 * Hook pour créer un period summary à trois parties
 */
export const useCreateThreePartiesMutation = (): UseMutationResult<
  ThreePartiesPeriodSummary,
  CreateThreePartiesParams
> =>
  useMutation(createThreeParties, {
    mutationKey: periodSummaryKeys.createThreeParties,
    onSuccess: () => {
      invalidatePeriodSummariesQueries()
    },
  })

// Methods
/**
 * Invalide toutes les queries liées aux period summaries
 */
export const invalidatePeriodSummariesQueries = (): void => {
  queryClient.invalidateQueries(['period-summaries'])
  queryClient.invalidateQueries(['missions/weeks-summary'])
  queryClient.invalidateQueries(['missions/weeks-summary/status'])
  queryClient.invalidateQueries(['worker-counters'])
}

export const useUpdateMutation = (): UseMutationResult<
  PeriodSummary,
  PeriodSummaryUpdatePayload
> =>
  useMutation(update, {
    mutationKey: periodSummaryKeys.update,
    onSuccess: () => {
      invalidatePeriodSummariesQueries()
    },
  })

export const useRequestCancellationMutation = (): UseMutationResult<
  PeriodSummary,
  { id: string } & RequestCancellationBody
> =>
  useMutation(
    ({ id, requestComment }) => requestCancellation({ id, requestComment }),
    {
      mutationKey: periodSummaryKeys.requestCancellation,
      onSuccess: () => {
        invalidatePeriodSummariesQueries()
      },
    },
  )

export const useResolveCancellationMutation = (): UseMutationResult<
  PeriodSummary,
  { id: string } & ResolveCancellationBody
> =>
  useMutation(
    ({ id, decision, resolutionComment }) =>
      resolveCancellationRequest({ id, decision, resolutionComment }),
    {
      mutationKey: periodSummaryKeys.resolveCancellation,
      onSuccess: () => {
        invalidatePeriodSummariesQueries()
      },
    },
  )
